import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { notify } from '@/lib/toast';
import { useProgramYearStore } from '@/stores/programYear';

import { useProgramYears } from '../hooks/useProgramYears';
import { isYearFreePath, readYearParam, YEAR_PARAM } from '../lib/year-param';

/**
 * 기준 연도 ↔ 주소·기억 동기화(specs/032 R8) — 워크스페이스 레이아웃에 1회 마운트, 렌더 없음.
 *
 * 1. 주소 `year` 가 **새로 바뀌었고** 선택 가능하면 명시 선택으로 기억한다(공유 링크 — FR-017). 바뀐 때만 채택하는 이유:
 *    헤더에서 "최근 연도 자동"을 고르면 기억 해제(동기)가 주소 갱신(비동기)보다 먼저 반영돼, 그 사이 남아 있는 주소 연도를
 *    다시 채택하면 선택이 되돌아간다.
 * 2. 주소 `year` 가 무효면 주소에서 지운다(기억·기본 연도로 열림).
 * 3. 기억된 연도가 있는데 주소에 없으면 붙인다 — 메뉴 이동·화면 자체 주소 갱신으로 빠져도 복원된다(FR-014·015).
 * 4. 기억된 연도가 선택지에서 사라졌으면 기억을 지우고 알린다(FR-018).
 *
 * 선택지를 받기 전에는 판단하지 않는다. 사업 상세는 자기 연도 고정이라 주소를 건드리지 않는다(FR-020).
 */
export function WorkspaceYearSync() {
  const { data } = useProgramYears();
  const explicitYear = useProgramYearStore((s) => s.explicitYear);
  const setExplicitYear = useProgramYearStore((s) => s.setExplicitYear);
  const clearExplicitYear = useProgramYearStore((s) => s.clearExplicitYear);
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const hasParam = searchParams.has(YEAR_PARAM);
  const urlYear = readYearParam(searchParams);
  /** 직전에 본 주소 연도 — undefined = 아직 못 봄(첫 진입은 주소 연도를 채택한다) */
  const seenUrlYear = useRef<null | number | undefined>(undefined);

  useEffect(() => {
    if (!data) return;
    const selectable = (year: number) => data.years.includes(year);

    if (explicitYear !== null && !selectable(explicitYear)) {
      clearExplicitYear();
      if (data.defaultYear !== null) {
        notify.info(
          `선택한 ${explicitYear}년 데이터가 없어 최근 연도(${data.defaultYear}년)로 전환했습니다.`,
        );
      }
    }
    if (isYearFreePath(pathname)) return;

    const urlChanged = seenUrlYear.current !== urlYear;
    seenUrlYear.current = urlYear;
    if (hasParam) {
      if (urlYear !== null && selectable(urlYear)) {
        if (urlChanged && urlYear !== explicitYear) setExplicitYear(urlYear);
        return;
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(YEAR_PARAM);
          return next;
        },
        { replace: true },
      );
      return;
    }
    if (explicitYear !== null && selectable(explicitYear)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(YEAR_PARAM, String(explicitYear));
          return next;
        },
        { replace: true },
      );
    }
  }, [
    clearExplicitYear,
    data,
    explicitYear,
    hasParam,
    pathname,
    setExplicitYear,
    setSearchParams,
    urlYear,
  ]);

  return null;
}
